import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Divider, Text } from "chameleon/lib/components";
import HelpTooltip from "chameleon/lib/components/help-tooltip/help-tooltip";

import { displayMonetaryValue } from "checkout-experience/components/common/monetary-util";
import { FormTitle } from "checkout-experience/components/forms/form-title";
import {
  firstColumnItem,
  grandTotalLine,
  lastColumnItem,
  primaryLine,
  summaryTable,
} from "checkout-experience/components/order-summary/order-summary.css";
import { SurchargeExtended } from "checkout-experience/hooks/use-surcharge";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { useCheckoutSession } from "checkout-query-client/hooks/use-checkout-session";

import { Money, PlainMessage } from "frontend-client";

interface OrderSummaryProps {
  order?: string;
  hideTitle?: boolean;
  surcharge?: SurchargeExtended | null;
}

const OrderSummary = (props: OrderSummaryProps) => {
  const { t, i18n } = useTranslation(["combined"]);
  const {
    checkoutSessionToken = "",
    apiHost = "",
    version = "",
  } = useChexContext();
  // Reading session from hook because the session is not being updated properly in HPP payment.tsx
  const { data: checkoutSession } = useCheckoutSession({
    apiHost,
    version,
    checkoutSessionToken,
  });

  const { orderSummary, shippingAddress } = checkoutSession ?? {};
  const { surcharge } = props;

  const totalAmount = useMemo(() => {
    if (
      (surcharge?.transactionFeeAmount ||
        surcharge?.purchaseTransactionFeePercent) &&
      orderSummary?.totalAmount
    ) {
      return {
        ...(orderSummary?.totalAmount ?? new Money()),
        amount:
          checkoutSession?.orderSummary?.totalWithSurcharge?.amount ===
          BigInt(0)
            ? surcharge?.transactionFeeAmount?.amount +
              orderSummary?.totalAmount?.amount
            : checkoutSession?.orderSummary?.totalWithSurcharge?.amount,
      } as PlainMessage<Money>;
    }
    return orderSummary?.totalAmount;
  }, [
    surcharge?.transactionFeeAmount,
    orderSummary?.totalAmount,
    surcharge?.purchaseTransactionFeePercent,
  ]);

  if (orderSummary?.totalAmount?.currencyCode) {
    const getSurchargeSummary = () => {
      const surchargeAmt =
        orderSummary.surchargeAmount ?? surcharge?.transactionFeeAmount;
      const surchargePercent = surcharge?.purchaseTransactionFeePercent ?? 0;
      if (surchargeAmt?.amount ?? 0 > 0) {
        return (
          <tr className={primaryLine}>
            <th scope="row" className={firstColumnItem}>
              <Text styleVariant="body" colorVariant="primaryText">
                {`${t("orderSummary.surcharge")}${
                  surchargePercent !== 0 ? `(${surchargePercent}%)` : ""
                }`}
              </Text>
              <HelpTooltip
                content={t("orderSummary.surchargeTooltip") ?? ""}
                position="top"
                size="medium"
                tooltipAriaLabel={t("tooltipAriaLabel")}
              />
            </th>
            <td className={lastColumnItem}>
              <Text styleVariant="body" colorVariant="primaryText">
                {displayMonetaryValue(surchargeAmt, i18n.language)}
              </Text>
            </td>
          </tr>
        );
      }
      return <></>;
    };

    return (
      <>
        {props.hideTitle ? null : <FormTitle title={t("orderSummary.title")} />}
        <table
          className={summaryTable}
          aria-label={
            "Order price consist of subtotal, shipping cost and sales tax"
          }
        >
          <tbody>
            <tr className={primaryLine}>
              <th scope="row" className={firstColumnItem}>
                <Text styleVariant="body" colorVariant="primaryText">
                  {t("orderSummary.subtotal")}
                </Text>
              </th>
              <td className={lastColumnItem}>
                <Text styleVariant="body" colorVariant="primaryText">
                  {displayMonetaryValue(
                    orderSummary.subtotalAmount,
                    i18n.language
                  )}
                </Text>
              </td>
            </tr>
            {(orderSummary.totalDiscount?.amount ?? 0 > 0) ? (
              <tr>
                <th scope="row" className={firstColumnItem}>
                  <Text styleVariant="body" colorVariant="positive">
                    {t("orderSummary.discount")}
                  </Text>
                </th>
                <td className={lastColumnItem}>
                  <Text styleVariant="body" colorVariant="positive">
                    -
                    {displayMonetaryValue(
                      orderSummary.totalDiscount,
                      i18n.language
                    )}
                  </Text>
                </td>
              </tr>
            ) : (
              <></>
            )}
            {(orderSummary.totalShippingCost?.amount ?? 0 > 0) ? (
              <tr className={primaryLine}>
                <th scope="row" className={firstColumnItem}>
                  <Text styleVariant="body" colorVariant="primaryText">
                    {t("orderSummary.shippingCost")}
                  </Text>
                </th>
                <td className={lastColumnItem}>
                  <Text styleVariant="body" colorVariant="primaryText">
                    {displayMonetaryValue(
                      orderSummary.totalShippingCost,
                      i18n.language
                    )}
                  </Text>
                </td>
              </tr>
            ) : (
              <></>
            )}
            {getSurchargeSummary()}
            {(orderSummary.totalTax?.amount ?? 0 > 0) ? (
              <tr className={primaryLine}>
                <th scope="row" className={firstColumnItem}>
                  <Text styleVariant="body" colorVariant="primaryText">
                    {`${t("orderSummary.estimatedSalesTax")}${shippingAddress?.administrativeArea ? ` (${shippingAddress?.administrativeArea})` : ""}`}
                  </Text>
                </th>
                <td className={lastColumnItem}>
                  <Text styleVariant="body" colorVariant="primaryText">
                    {displayMonetaryValue(orderSummary.totalTax, i18n.language)}
                  </Text>
                </td>
              </tr>
            ) : (
              <></>
            )}
          </tbody>
        </table>
        <Divider noMargins />
        <table className={summaryTable} aria-label={"Order grand total"}>
          <tbody>
            <tr className={grandTotalLine}>
              <th scope="row" className={firstColumnItem}>
                <Text styleVariant="heading3" colorVariant="primaryText">
                  {t("orderSummary.grandTotal")}
                </Text>
              </th>
              <td className={lastColumnItem}>
                <Text styleVariant="heading3" colorVariant="primaryText">
                  {displayMonetaryValue(totalAmount, i18n.language)}
                </Text>
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  } else {
    return (
      <>
        {props.hideTitle ? null : <FormTitle title={t("orderSummary.title")} />}
        <Text styleVariant="body">
          {t("orderSummary.orderSummaryCannotBeCalculated")}
        </Text>
      </>
    );
  }
};

export default OrderSummary;
